/* eslint-disable max-len */
import { useEffect, useRef, useState } from "react";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import Check from "@mui/icons-material/Check";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import ArticleIcon from "@mui/icons-material/Article";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { ToastContainer, toast } from "react-toastify";
import { Link } from "react-router-dom";
import styles from "./TeacherRewards.module.css";
import rewardAnimationHtml from "./rewardAnimationHtml";
import { openSignupWithMarketingParams } from "../../utils/signupMarketingUrl";

const FEATURE_ICONS = ["💰", "📊", "🎯", "🏫", "🏅", "⚡"];

const FEATURES = [
  {
    title: "Earn monthly rewards based on your activity",
    body:
      "Your consistency builds into real monetary benefits paid out every month — automatically."
  },
  {
    title: "Turn routine school tasks into measurable value",
    body:
      "Every task you complete inside Shelf21 is tracked, counted, and converted into points that matter."
  },
  {
    title: "Get recognized for your consistency and effort",
    body:
      "Your steady daily work is finally visible. No more disappearing into the background unnoticed."
  },
  {
    title: "Build motivation through visible progress",
    body:
      "Watch your points grow in real time. A live dashboard that reflects exactly how much you've done."
  },
  {
    title: "Feel appreciated for the work you already do",
    body:
      "This system was built specifically because teachers deserve more than just a thank-you at the end of term."
  },
  {
    title: "Create a fair system where effort actually counts",
    body:
      "No favoritism. No opinions. Just your actual contribution — tracked and rewarded objectively."
  }
];

const EARNING_ICONS = ["📋", "📄", "📝", "🔢", "⚙️"];

const EARNING_ACTIVITIES = [
  { title: "Mark Attendance", body: "Daily class attendance logging" },
  { title: "Upload Lesson Notes", body: "Share notes for each class" },
  { title: "Create Assignments", body: "Build and assign student work" },
  { title: "Add Exam Questions", body: "Contribute to the question bank" },
  { title: "Complete Academic Tasks", body: "Any eligible activity inside the system" }
];

const MOTIVATE_BULLETS = [
  "Full visibility into teacher activity and contribution",
  "Objective, data-driven performance measurement",
  "Boost staff retention and daily motivation",
  "Available on Shelf21 Pro — refer your school to learn more"
];

const DASHBOARD_BARS = [
  { label: "Attendance Rate", pct: 94 },
  { label: "Notes Uploaded", pct: 78 },
  { label: "Assignments Created", pct: 85 },
  { label: "Exam Bank Contributions", pct: 61 }
];

const DASHBOARD_PREVIEW_TASKS = [
  { label: "Attendance Marked", Icon: TaskAltIcon },
  { label: "Lesson Note Uploaded", Icon: ArticleIcon },
  { label: "Assignment Created", Icon: AssignmentIcon }
];

const WHAT_IS_EXPLAINER_CARD_STEPS = [
  "Log in to Shelf21 as usual",
  "Complete normal school tasks",
  "Points added automatically",
  "Convert points into rewards & income"
];

const TEACHER_FAQ = [
  {
    q: "Do I have to do extra work to earn rewards?",
    a: "No. Shelf21 adds points when you complete normal in-app tasks like attendance, lesson notes, and assignments — nothing beyond your usual workflow."
  },
  {
    q: "How do I start earning points?",
    a: "Use Shelf21 as you already do. Each eligible activity you complete inside the platform can add points to your account automatically."
  },
  {
    q: "Can these points really turn into money?",
    a: "Yes. Schools configure how points convert into monetary benefits and incentives. Your admin can share the active reward rules for your institution."
  },
  {
    q: "What makes this different from normal teacher incentives?",
    a: "Progress is tied to real activity in Shelf21, so recognition is transparent and consistent — not based on opinion or one-off contests."
  },
  {
    q: "What if I'm already doing these activities daily?",
    a: "That is the goal. The system rewards the work you are already doing, so consistency finally shows up in data and rewards."
  },
  {
    q: "Can my school join the Teacher Reward System?",
    a: "Schools using Shelf21 Pro can participate. School owners and admins can use Refer your School to get connected and configure the program."
  }
];

/** Looping confetti on the hero (right-hand side). Values are deterministic from index for stable hydration. */
const HERO_CONFETTI_COUNT = 34;

function heroConfettiStyle(i, compact) {
  const palettes = ["rgba(255,255,255,0.52)", "rgba(255,204,0,0.48)", "rgba(226,239,255,0.62)"];
  const leftPct = compact
    ? 40 + (((i * 47) >> 3) % 26)
    : 14 + (((i * 47) >> 3) % 72);
  const left = `${leftPct}%`;
  const w = `${3 + (i % 4)}px`;
  const h = `${5 + ((i >> 2) % 9)}px`;
  const driftMid = compact
    ? `${-2 + (((i * 19) >> 2) % 8)}px`
    : `${-8 + (((i * 19) >> 2) % 28)}px`;
  const driftEnd = compact
    ? `${5 + (((i * 31) >> 3) % 12)}px`
    : `${18 + (((i * 31) >> 3) % 36)}px`;
  const fade = `${0.34 + (((i * 13) >> 4) % 10) / 100}`;
  const durBase = compact ? 11 : 15;
  const durExtra = compact ? (((i * 37) >> 4) % 8) : (((i * 37) >> 4) % 12);

  return {
    left,
    width: w,
    height: h,
    backgroundColor: palettes[i % 3],
    borderRadius: i % 3 === 0 ? "50%" : `${1 + (i % 3)}px`,
    animationDuration: `${durBase + durExtra}s`,
    animationDelay: `${-(((i * 97) >> 5) % 160) / 10}s`,
    "--cfc-mid": driftMid,
    "--cfc-end": driftEnd,
    "--cfc-fade": fade
  };
}

const TeacherRewardsPage = () => {
  const motivateSectionRef = useRef(null);
  const [dashboardBarsActive, setDashboardBarsActive] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [animationIframeSrc, setAnimationIframeSrc] = useState("");
  const [heroConfettiCompact, setHeroConfettiCompact] = useState(false);

  const referralHref = `${process.env.REACT_APP_URL || ""}/referral/generatelink`;

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return;
    }
    const mq = window.matchMedia("(max-width: 600px)");
    const onChange = () => setHeroConfettiCompact(mq.matches);

    onChange();
    mq.addEventListener("change", onChange);

    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (typeof rewardAnimationHtml !== "string" || !rewardAnimationHtml.length) {
      return;
    }
    let url = "";
    try {
      const blob = new Blob([rewardAnimationHtml], { type: "text/html;charset=utf-8" });
      url = URL.createObjectURL(blob);
      setAnimationIframeSrc(url);
    } catch {
      setAnimationIframeSrc("");
    }

    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, []);

  useEffect(() => {
    document.title = "Teacher Reward System | Shelf21";
  }, []);

  useEffect(() => {
    const link = document.createElement("link");
    link.href = "https://assets.calendly.com/assets/external/widget.css";
    link.rel = "stylesheet";
    document.head.appendChild(link);

    const script = document.createElement("script");
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    const node = motivateSectionRef.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setDashboardBarsActive(true);
        });
      },
      { threshold: 0.22, rootMargin: "0px 0px -8% 0px" }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  const toggleFaq = (index) => {
    setOpenFaqIndex((prev) => (prev === index ? null : index));
  };

  const handleGetStarted = () => {
    openSignupWithMarketingParams({
      utm_source: "facebook",
      utm_campaign: "teacher_rewards"
    });
  };

  const handleBookDemo = (e) => {
    e.preventDefault();
    if (window.Calendly && typeof window.Calendly.initPopupWidget === "function") {
      window.Calendly.initPopupWidget({
        url: process.env.REACT_APP_CALENDLY_URL
      });
    } else {
      toast.info("Calendly widget is still loading. Please try again shortly.");
    }
  };

  return (
    <div className={styles.pageRoot}>
      <section className={styles.blueSection}>
        <div className={styles.heroConfettiBackdrop} aria-hidden>
          {Array.from({ length: HERO_CONFETTI_COUNT }).map((_, i) => (
            <span
              key={i}
              className={`${styles.heroConfettiPiece} ${heroConfettiCompact ? styles.heroConfettiPieceTight : ""}`}
              style={heroConfettiStyle(i, heroConfettiCompact)}
            />
          ))}
        </div>
        <div className={styles.container}>
          <div className={styles.badge}>
            <span aria-hidden="true">🏆</span>
            <span>Teacher Reward System</span>
          </div>
          <h1 className={styles.heroTitle}>
            Finally Get <span className={styles.highlightYellow}>Rewarded</span> for the Work You Already Do
          </h1>
          <p className={styles.heroSubtitle}>
            Mark attendance, upload lesson notes, create assignments — and earn points that turn into real rewards,
            incentives, and extra monthly income.
          </p>
          <div className={styles.ctaRow}>
            <a
              className={styles.btnPrimary}
              href={referralHref}
              target="_blank"
              rel="noopener noreferrer"
            >
              Refer your School
              <NorthEastIcon fontSize="small" aria-hidden />
            </a>
          </div>
        </div>
      </section>

      <section className={styles.whatYouGetSection}>
        <div className={`${styles.container} ${styles.centered}`}>
          <span className={styles.sectionLabel}>What you get</span>
          <h2 className={styles.sectionTitle}>
            Everything You Deserve for Your <span className={styles.highlightBlue}>Consistency</span>
          </h2>
          <p className={styles.sectionLead}>
            Your daily effort is finally visible, measurable, and rewarded — automatically.
          </p>
          <div className={styles.featureGrid}>
            {FEATURES.map((f, i) => (
              <article key={f.title} className={styles.featureCard}>
                <div className={styles.iconWrap} aria-hidden>
                  {FEATURE_ICONS[i]}
                </div>
                <h3>{f.title}</h3>
                <p>{f.body}</p>
              </article>
            ))}
          </div>
          <div className={styles.whatYouGetCtaWrap}>
            <a
              className={styles.dashboardPreviewCta}
              href={referralHref}
              target="_blank"
              rel="noopener noreferrer"
            >
              Refer your School
              <NorthEastIcon fontSize="small" aria-hidden />
            </a>
          </div>
        </div>
      </section>

      <section className={styles.trsExplainerSection} aria-labelledby="trs-explainer-heading">
        <div className={styles.container}>
          <div className={styles.trsExplainerGrid}>
            <div className={styles.trsExplainerCopy}>
              <span className={styles.trsExplainerBadge}>What is the Teacher Reward System?</span>
              <h2 id="trs-explainer-heading" className={styles.trsExplainerTitle}>
                Simple.{" "}
                <span className={styles.trsExplainerTitleBlue}>No Extra Work.</span>
                <span className={styles.trsExplainerTitleBreak}>
                  <span className={styles.highlightYellow}>Just Reward</span>
                </span>
              </h2>
              <p className={styles.trsExplainerIntro}>
                The Teacher Reward System helps your school reward your teachers for completing important academic
                activities consistently.
              </p>
              <ol className={styles.trsExplainerNumbered}>
                <li>
                  <span className={styles.trsExplainerListNum} aria-hidden>
                    1
                  </span>
                  <span>
                    You complete your <strong>normal school tasks inside Shelf21…</strong>
                  </span>
                </li>
                <li>
                  <span className={styles.trsExplainerListNum} aria-hidden>
                    2
                  </span>
                  <span>You earn points.</span>
                </li>
                <li>
                  <span className={styles.trsExplainerListNum} aria-hidden>
                    3
                  </span>
                  <span>
                    Those points can then be converted into{" "}
                    <strong>rewards, incentives, and monetary benefits</strong> by Shelf21.
                  </span>
                </li>
              </ol>
              <p className={styles.trsExplainerClosing}>No extra work. No complicated process.</p>
              <p className={styles.trsExplainerClosingStrong}>
                <strong>Just rewards for the work you already do every day.</strong>
              </p>
              <a
                className={styles.dashboardPreviewCta}
                href={referralHref}
                target="_blank"
                rel="noopener noreferrer"
              >
                Refer your School
                <NorthEastIcon fontSize="small" aria-hidden />
              </a>
            </div>

            <div className={styles.trsHowCard} aria-label="How the Teacher Reward System works">
              <p className={styles.trsHowCardKicker}>How it works</p>
              <ol className={styles.trsHowCardSteps}>
                {WHAT_IS_EXPLAINER_CARD_STEPS.map((label, i) => {
                  const isLast = i === WHAT_IS_EXPLAINER_CARD_STEPS.length - 1;

                  return (
                    <li key={label} className={styles.trsHowCardStep}>
                      <div className={styles.trsHowCardStepTrack}>
                        <span className={styles.trsHowCardStepBadge}>{i + 1}</span>
                        {!isLast ? (
                          <span className={styles.trsHowCardStepConnector} aria-hidden />
                        ) : null}
                      </div>
                      <span className={styles.trsHowCardStepText}>{label}</span>
                    </li>
                  );
                })}
              </ol>
              <div className={styles.trsHowCardFooter}>
                <GpsFixedIcon className={styles.trsHowCardFooterIcon} aria-hidden />
                <span>No extra work. No complicated process.</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.earningSectionBlue}>
        <div className={`${styles.container} ${styles.centered}`}>
          <span className={styles.sectionLabel}>Earning points</span>
          <h2 className={styles.sectionTitle}>
            Every Time You Complete{" "}
            <span className={styles.highlightYellow}>Basic Teaching Tasks Like</span> Marking Attendance, Sharing
            Lesson Notes, and More
          </h2>
          <p className={styles.sectionLead}>
            You earn points automatically. The more of your school tasks you complete, the more points you accumulate.
          </p>
        </div>

        <div className={styles.earningMarqueeOuter}>
          <div className={styles.earningMarqueeTrack}>
            {[0, 1].flatMap((dup) => EARNING_ACTIVITIES.map((item, i) => (
              <article
                key={`${dup}-${item.title}-${i}`}
                className={`${styles.featureCard} ${styles.earningMarqueeCard}`}
              >
                <div className={styles.iconWrap} aria-hidden>
                  {EARNING_ICONS[i]}
                </div>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </article>
            )))}
          </div>
        </div>

        <div className={styles.earningReferCtaBelowMarquee}>
          <a
            className={styles.dashboardPreviewCta}
            href={referralHref}
            target="_blank"
            rel="noopener noreferrer"
          >
            Refer your School
            <NorthEastIcon fontSize="small" aria-hidden />
          </a>
        </div>
      </section>

      <section className={styles.dashboardPreviewSection} aria-labelledby="dashboard-preview-heading">
        <div className={styles.container}>
          <div className={styles.dashboardPreviewGrid}>
            <div className={styles.dashboardPreviewCopy}>
              <span className={styles.dashboardPreviewBadge}>Imagine this</span>
              <h2 id="dashboard-preview-heading" className={styles.dashboardPreviewTitle}>
                You Finish Your School <span className={styles.highlightBlue}>Activities</span> for the Day...
              </h2>
              <p className={styles.dashboardPreviewSubhead}>Then you open your dashboard and see:</p>
              <div className={styles.dashboardPreviewTasks} role="list">
                {DASHBOARD_PREVIEW_TASKS.map(({ label, Icon }) => (
                  <div key={label} className={styles.dashboardPreviewTaskRow} role="listitem">
                    <span className={styles.dashboardPreviewTaskIcon} aria-hidden>
                      <Icon style={{ fontSize: 22, color: "#64748b" }} />
                    </span>
                    <span className={styles.dashboardPreviewTaskLabel}>{label}</span>
                    <span className={styles.pointsAddedBadge}>Points Added</span>
                  </div>
                ))}
              </div>
              <p className={styles.dashboardPreviewBody}>
                For the first time, your daily activities are building toward rewards, incentives, and extra monthly
                benefits.
              </p>
              <p className={styles.dashboardPreviewBody}>Your work no longer disappears unnoticed.</p>
              <a
                className={styles.dashboardPreviewCta}
                href={referralHref}
                target="_blank"
                rel="noopener noreferrer"
              >
                Refer your School
                <NorthEastIcon fontSize="small" aria-hidden />
              </a>
            </div>
            <div className={styles.dashboardPreviewVisual}>
              <div className={styles.dashboardPreviewFrame}>
                <div className={styles.dashboardPreviewIframeWrap}>
                  {animationIframeSrc ? (
                    <iframe
                      title="Shelf21 rewards dashboard preview animation"
                      className={styles.dashboardPreviewIframe}
                      src={animationIframeSrc}
                      loading="lazy"
                      sandbox="allow-scripts"
                    />
                  ) : (
                    <iframe
                      title="Shelf21 rewards dashboard preview animation"
                      className={styles.dashboardPreviewIframe}
                      srcDoc={rewardAnimationHtml}
                      loading="lazy"
                      sandbox="allow-scripts"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section ref={motivateSectionRef} className={styles.motivateSection}>
        <div className={styles.container}>
          <div className={styles.motivateSplit}>
            <div className={styles.motivateCopy}>
              <span className={styles.badgeYellow}>For school owners &amp; admins</span>
              <h2 className={styles.motivateTitle}>Motivate Your Best Asset — Your Teachers</h2>
              <p className={styles.motivateLead}>
                Your teachers carry your school. Give them a system that makes them feel it.
              </p>
              <ul className={styles.checklist}>
                {MOTIVATE_BULLETS.map((line) => (
                  <li key={line}>
                    <span className={styles.checkBubble} aria-hidden>
                      <Check style={{ fontSize: 14 }} />
                    </span>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
              <div className={styles.motivateCtaRow}>
                <button type="button" className={styles.btnPrimary} onClick={handleGetStarted}>
                  Sign up
                  <NorthEastIcon fontSize="small" aria-hidden />
                </button>
                <a href="#demo" className={styles.btnGhost} onClick={handleBookDemo}>
                  Book a Demo
                  <BookmarkBorderIcon fontSize="small" aria-hidden />
                </a>
              </div>
            </div>

            <div className={styles.dashboardPanel} aria-label="School overview preview">
              <p className={styles.dashboardTitle}>School overview — this month</p>
              <div className={styles.statRow}>
                <div className={styles.statCard}>
                  <div className={styles.statValue}>47</div>
                  <div className={styles.statLabel}>Active Teachers</div>
                </div>
                <div className={styles.statCard}>
                  <div className={styles.statValue}>3,840</div>
                  <div className={styles.statLabel}>Tasks Completed</div>
                </div>
              </div>
              <div className={styles.barRows}>
                {DASHBOARD_BARS.map((bar) => (
                  <div key={bar.label} className={styles.barRow}>
                    <span className={styles.barLabel}>{bar.label}</span>
                    <div className={styles.barTrack}>
                      <div
                        className={styles.barFill}
                        style={{ width: dashboardBarsActive ? `${bar.pct}%` : "0%" }}
                      />
                    </div>
                    <span className={styles.barPercent}>{bar.pct}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.faqSection}>
        <div className={`${styles.container} ${styles.centered}`}>
          <span className={styles.sectionLabel}>FAQ</span>
          <h2 className={styles.sectionTitle}>
            Questions? We&apos;ve Got <span className={styles.highlightBlue}>Answers.</span>
          </h2>
          <div className={styles.faqList}>
            {TEACHER_FAQ.map((item, i) => (
              <div key={item.q} className={styles.faqItem}>
                <button
                  type="button"
                  className={styles.faqQuestion}
                  onClick={() => toggleFaq(i)}
                  aria-expanded={openFaqIndex === i}
                >
                  <span>{item.q}</span>
                  <span className={styles.faqIcon} aria-hidden>
                    {openFaqIndex === i ? "−" : "+"}
                  </span>
                </button>
                {openFaqIndex === i ? (
                  <div className={styles.faqAnswer}>{item.a}</div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.finalCtaSection}>
        <div className={styles.container}>
          <h2 className={styles.finalCtaTitle}>
            Your Daily Work Deserves to Be <span className={styles.highlightYellow}>Rewarded</span>
          </h2>
          <p className={styles.finalCtaSubtitle}>
            Join thousands of teachers already earning points for the work they already do every day.
          </p>
          <div className={styles.ctaRow}>
            <a
              className={`${styles.btnPrimary} ${styles.btnPill}`}
              href={referralHref}
              target="_blank"
              rel="noopener noreferrer"
            >
              Refer your School
              <NorthEastIcon fontSize="small" aria-hidden />
            </a>
          </div>
          <p className={styles.finalCtaDisclaimer}>
            Terms &amp; Conditions apply. Available on Shelf21 Pro Plan.&nbsp;
            <Link className={styles.termsLink} to="/teacher-rewards/terms-and-conditions">
              Read T&amp;Cs →
            </Link>
          </p>
        </div>
      </section>

      <ToastContainer />
    </div>
  );
};

export default TeacherRewardsPage;
